import { supabase } from '@/lib/supabase';
import type { Profile } from '@/types';

/** Data layer for the inside-community view (`/c/:id`). Reproduces the OG
 *  community.js Supabase surface: community_messages, community_members,
 *  user_presence (online dots), live_rooms, reports. */

type MiniProfile = Pick<Profile, 'id' | 'display_name' | 'avatar_url' | 'handle' | 'role' | 'email'>;

async function fetchProfiles(ids: string[]): Promise<Map<string, MiniProfile>> {
  const map = new Map<string, MiniProfile>();
  if (!ids.length) return map;
  const { data } = await supabase
    .from('profiles')
    .select('id, display_name, avatar_url, handle, role, email')
    .in('id', ids);
  for (const p of data ?? []) map.set(p.id, p);
  return map;
}

async function fetchOnline(ids: string[]): Promise<Set<string>> {
  const online = new Set<string>();
  if (!ids.length) return online;
  const { data } = await supabase
    .from('user_presence')
    .select('user_id, status')
    .in('user_id', ids)
    .eq('status', 'online');
  for (const r of data ?? []) online.add(r.user_id);
  return online;
}

export interface ViewMessage {
  id: string;
  author_id: string;
  body: string | null;
  /** Raw `media` column: either an external URL (GIF) or a private storage path. */
  media: string | null;
  /** Display-ready URL: external URLs pass through, storage paths become signed URLs. */
  mediaUrl: string | null;
  channel_id: string | null;
  reply_to_id: string | null;
  created_at: string;
  edited_at: string | null;
  author: MiniProfile | null;
}

const MEDIA_BUCKET = 'message-media';
const SIGNED_URL_TTL = 60 * 60; // 1 hour

/** Media is external (GIF/remote) when it's an http(s) URL; otherwise it's a
 *  path inside the private `message-media` bucket that must be signed to view. */
function isExternalMedia(media: string): boolean {
  return /^https?:\/\//i.test(media);
}

/** Resolve every message's `media` into a viewable `mediaUrl`: external URLs
 *  pass through untouched, storage paths are batch-signed in one request. */
async function resolveMediaUrls(medias: string[]): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  const paths = medias.filter((m) => !isExternalMedia(m));
  for (const m of medias) if (isExternalMedia(m)) out.set(m, m);
  if (paths.length) {
    const { data } = await supabase.storage
      .from(MEDIA_BUCKET)
      .createSignedUrls([...new Set(paths)], SIGNED_URL_TTL);
    for (const item of data ?? []) {
      if (item.path && item.signedUrl) out.set(item.path, item.signedUrl);
    }
  }
  return out;
}

/** Loads the whole community's history (like the OG); the view filters by
 *  channel client-side via `(channel_id || 'general')` to tolerate legacy rows
 *  with an unset channel_id. */
export async function listMessages(communityId: string): Promise<ViewMessage[]> {
  const { data, error } = await supabase
    .from('community_messages')
    .select('id, author_id, body, media, channel_id, reply_to_id, created_at, edited_at')
    .eq('community_id', communityId)
    .order('created_at', { ascending: true })
    .limit(400);
  if (error) throw error;
  const rows = data ?? [];
  const [authors, mediaUrls] = await Promise.all([
    fetchProfiles([...new Set(rows.map((r) => r.author_id))]),
    resolveMediaUrls(rows.map((r) => r.media).filter((m): m is string => Boolean(m))),
  ]);
  return rows.map((r) => ({
    ...r,
    author: authors.get(r.author_id) ?? null,
    mediaUrl: r.media ? (mediaUrls.get(r.media) ?? null) : null,
  }));
}

export async function sendMessage(input: {
  communityId: string;
  channelId: string;
  authorId: string;
  body: string;
  media?: string | null;
  replyToId?: string | null;
}): Promise<void> {
  const { error } = await supabase.from('community_messages').insert({
    community_id: input.communityId,
    channel_id: input.channelId,
    author_id: input.authorId,
    body: input.body,
    media: input.media ?? null,
    reply_to_id: input.replyToId ?? null,
  });
  if (error) throw error;
}

/** Upload an image attachment to the private `message-media` bucket and return
 *  its storage path (stored in `community_messages.media`, signed on read). */
export async function uploadMessageImage(file: File, communityId: string): Promise<string> {
  const ext = (file.name.split('.').pop() || 'png').toLowerCase().replace(/[^a-z0-9]/g, '');
  const path = `community/${communityId}/${crypto.randomUUID()}.${ext || 'png'}`;
  const { error } = await supabase.storage
    .from(MEDIA_BUCKET)
    .upload(path, file, { contentType: file.type || 'image/png', upsert: false });
  if (error) throw error;
  return path;
}

const UUID_RE = /^[0-9a-f-]{36}$/i;

/** Edit a message body (own messages only; RLS enforces author). Guarded to real
 *  uuid ids so optimistic local rows are never sent. */
export async function editMessage(id: string, body: string): Promise<void> {
  if (!UUID_RE.test(id)) return;
  const { error } = await supabase
    .from('community_messages')
    .update({ body, edited_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function deleteMessage(id: string): Promise<void> {
  if (!UUID_RE.test(id)) return;
  const { error } = await supabase.from('community_messages').delete().eq('id', id);
  if (error) throw error;
}

export interface ReactionRow {
  message_id: string;
  user_id: string;
  emoji: string;
}

/** All reactions for the given messages (server-side, shared across users). */
export async function listReactions(messageIds: string[]): Promise<ReactionRow[]> {
  if (!messageIds.length) return [];
  const { data, error } = await supabase
    .from('community_message_reactions')
    .select('message_id, user_id, emoji')
    .in('message_id', messageIds);
  if (error) throw error;
  return data ?? [];
}

/** Toggle one emoji on a message for a user: removes it if already present,
 *  otherwise adds it. RLS guarantees a user can only touch their own rows. */
export async function toggleReaction(
  messageId: string,
  userId: string,
  emoji: string,
): Promise<void> {
  if (!UUID_RE.test(messageId)) return;
  const { data: existing } = await supabase
    .from('community_message_reactions')
    .select('message_id')
    .eq('message_id', messageId)
    .eq('user_id', userId)
    .eq('emoji', emoji)
    .maybeSingle();

  if (existing) {
    const { error } = await supabase
      .from('community_message_reactions')
      .delete()
      .eq('message_id', messageId)
      .eq('user_id', userId)
      .eq('emoji', emoji);
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('community_message_reactions')
      .insert({ message_id: messageId, user_id: userId, emoji });
    if (error) throw error;
  }
}

export type MemberRole = 'OWNER' | 'ADMIN' | 'MEMBER';
export interface ViewMember {
  user_id: string;
  role: MemberRole;
  online: boolean;
  member_number: number | null;
  founding: boolean;
  profile: MiniProfile | null;
}

export async function listMembers(
  communityId: string,
  ownerId: string | null,
): Promise<ViewMember[]> {
  const { data, error } = await supabase
    .from('community_members')
    .select('user_id, member_number, founding')
    .eq('community_id', communityId)
    .order('member_number', { ascending: true });
  if (error) throw error;
  const rows = data ?? [];

  const ids = new Set(rows.map((r) => r.user_id));
  if (ownerId) ids.add(ownerId);
  const idList = [...ids];

  const [profiles, online] = await Promise.all([fetchProfiles(idList), fetchOnline(idList)]);

  const members: ViewMember[] = idList.map((uid) => {
    const row = rows.find((r) => r.user_id === uid);
    const profile = profiles.get(uid) ?? null;
    const isOwner = uid === ownerId;
    const isDesignatedWinrAdmin =
      communityId === 'winr-official' &&
      (profile?.handle?.toLowerCase() === 'neotpf' ||
        profile?.email?.toLowerCase() === 'tonyvivixofficial@icloud.com');
    const role: MemberRole = isOwner
      ? 'OWNER'
      : isDesignatedWinrAdmin || /admin|mod/i.test(profile?.role ?? '')
        ? 'ADMIN'
        : 'MEMBER';
    return {
      user_id: uid,
      role,
      online: isOwner || online.has(uid),
      member_number: row?.member_number ?? null,
      founding: row?.founding ?? false,
      profile,
    };
  });

  // Owner first, then by membership number.
  return members.sort((a, b) => {
    if (a.role === 'OWNER') return -1;
    if (b.role === 'OWNER') return 1;
    return (a.member_number ?? 1e9) - (b.member_number ?? 1e9);
  });
}

export async function getMembership(communityId: string, userId: string): Promise<boolean> {
  const { data } = await supabase
    .from('community_members')
    .select('user_id')
    .eq('community_id', communityId)
    .eq('user_id', userId)
    .maybeSingle();
  return Boolean(data);
}

export async function joinCommunity(communityId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from('community_members')
    .insert({ community_id: communityId, user_id: userId });
  if (error && error.code !== '23505') throw error; // ignore "already a member"
}

export async function leaveCommunity(communityId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from('community_members')
    .delete()
    .eq('community_id', communityId)
    .eq('user_id', userId);
  if (error) throw error;
}

export interface ViewRoom {
  id: string;
  topic: string | null;
  host_id: string;
}

export async function listLiveRooms(communityId: string): Promise<ViewRoom[]> {
  const { data, error } = await supabase
    .from('live_rooms')
    .select('id, topic, host_id')
    .eq('community_id', communityId)
    .eq('is_live', true);
  if (error) throw error;
  return data ?? [];
}

export async function submitReport(input: {
  communityId: string;
  targetType: string;
  targetId: string;
  reason: string;
  note: string;
  reporterId: string;
}): Promise<void> {
  const reason = input.note ? `${input.reason} — ${input.note}` : input.reason;
  const { error } = await supabase.from('reports').insert({
    community_id: input.communityId,
    target_type: input.targetType,
    target_id: input.targetId,
    reason,
    reporter_id: input.reporterId,
  });
  if (error) throw error;
}
